/**
 * Görev başına UYAP istek telemetrisi.
 *
 * Ağ süresi ile yapay uykuyu ayırır, UYAP kısıt kodlarını ve hata veren uçları
 * sayar. UYAP rate limit'i 429 ile değil, 200 + errorCode / login HTML'i ile
 * bildirdiği için tek sinyal budur.
 *
 * In-memory ve tekildir; ayrı HTTP isteği atmaz, özet putTask payload'ına eklenir.
 */

export interface IstekKaydi {
    ad: string;
    agMs: number;
    uykuMs: number;
    hata?: string;
}

/** Hatanın hangi katmanda oluştuğu. Mesaj task.description'da tutulur. */
export type HataTuru =
    | 'istek'    // ağ / HTTP seviyesi (timeout, bağlantı, 5xx)
    | 'cevap'    // prepareResponseData: beklenmeyen içerik tipi, XML/JSON çözümlenemedi
    | 'parse'    // parse(): cevap geldi ama BEKLENEN ŞEKİLDE DEĞİL
    | 'uyap';    // UYAP'ın kendi errorCode'u

export interface GorevMetrikleri {
    istekSayisi: number;
    toplamAgMs: number;
    toplamUykuMs: number;
    /** Yapay uykunun toplam UYAP süresi içindeki payı (0-1). */
    uykuOrani: number;
    retrySayisi: number;
    oturumKurtarmaSayisi: number;
    beklenmeyenHtmlSayisi: number;
    /** UYAP errorCode -> kaç kez görüldü. Örn. {"PRTL_GNL_10001-61": 2} */
    kisitKodlari: Record<string, number>;
    /**
     * Hata veren uçlar: "katman:uc_adi" -> kaç kez.
     * parse: ile başlayanlar UYAP format değişikliğinin ilk işaretidir.
     */
    hataliUclar: Record<string, number>;
    /** En yavaş istekler (ağ süresine göre), tanı için. */
    enYavaslar: IstekKaydi[];
}

/** Payload'ı şişirmemek için saklanan en yavaş istek sayısı. */
const EN_YAVAS_LIMIT = 5;
/** Saklanacak farklı "katman:uc" anahtarı sayısı. */
const HATA_LIMIT = 20;

class IstekOlcumuToplayici {
    private istekSayisi = 0;
    private toplamAgMs = 0;
    private toplamUykuMs = 0;
    private retrySayisi = 0;
    private oturumKurtarmaSayisi = 0;
    private beklenmeyenHtmlSayisi = 0;
    private kisitKodlari: Record<string, number> = {};
    private enYavaslar: IstekKaydi[] = [];
    private hataliUclar: Record<string, number> = {};

    /** TaskHandler.handle() başında çağrılır. */
    sifirla(): void {
        this.istekSayisi = 0;
        this.toplamAgMs = 0;
        this.toplamUykuMs = 0;
        this.retrySayisi = 0;
        this.oturumKurtarmaSayisi = 0;
        this.beklenmeyenHtmlSayisi = 0;
        this.kisitKodlari = {};
        this.enYavaslar = [];
        this.hataliUclar = {};
    }

    /** Hata veren ucun adını kaydeder. "En yavaş" listesinden bağımsızdır. */
    hataKaydet(tur: HataTuru, ad: string): void {
        const anahtar = tur + ':' + ad;
        if (this.hataliUclar[anahtar] === undefined && Object.keys(this.hataliUclar).length >= HATA_LIMIT) {
            return;
        }
        this.hataliUclar[anahtar] = (this.hataliUclar[anahtar] || 0) + 1;
    }

    istekKaydet(ad: string, agMs: number, uykuMs: number, hata?: string): void {
        this.istekSayisi++;
        this.toplamAgMs += agMs;
        this.toplamUykuMs += uykuMs;

        const kayit: IstekKaydi = {ad, agMs: Math.round(agMs), uykuMs: Math.round(uykuMs)};
        if (hata)
            kayit.hata = hata;

        // Sıralı ekleme: liste zaten en fazla EN_YAVAS_LIMIT uzunlukta.
        this.enYavaslar.push(kayit);
        this.enYavaslar.sort((a, b) => b.agMs - a.agMs);
        if (this.enYavaslar.length > EN_YAVAS_LIMIT)
            this.enYavaslar.length = EN_YAVAS_LIMIT;
    }

    kisitKaydet(kod: string): void {
        if (!kod)
            return;
        this.kisitKodlari[kod] = (this.kisitKodlari[kod] || 0) + 1;
    }

    retryKaydet(): void {
        this.retrySayisi++;
    }

    oturumKurtarmaKaydet(): void {
        this.oturumKurtarmaSayisi++;
    }

    beklenmeyenHtmlKaydet(): void {
        this.beklenmeyenHtmlSayisi++;
    }

    ozet(): GorevMetrikleri {
        const toplam = this.toplamAgMs + this.toplamUykuMs;
        return {
            istekSayisi: this.istekSayisi,
            toplamAgMs: Math.round(this.toplamAgMs),
            toplamUykuMs: Math.round(this.toplamUykuMs),
            uykuOrani: toplam > 0 ? Math.round((this.toplamUykuMs / toplam) * 100) / 100 : 0,
            retrySayisi: this.retrySayisi,
            oturumKurtarmaSayisi: this.oturumKurtarmaSayisi,
            beklenmeyenHtmlSayisi: this.beklenmeyenHtmlSayisi,
            kisitKodlari: {...this.kisitKodlari},
            hataliUclar: {...this.hataliUclar},
            enYavaslar: this.enYavaslar.map(k => ({...k}))
        };
    }
}

export const istekOlcumu = new IstekOlcumuToplayici();
